import * as Headless from '@headlessui/react'
import { classNames } from '@/Utils/Utils'
import React from 'react'

export function Fieldset({ className, ...props }: { className?: string } & Omit<Headless.FieldsetProps, 'className'>) {
    return <Headless.Fieldset {...props} className={classNames(className, '*:data-[slot=text]:mt-1 [&>*+[data-slot=control]]:mt-6')} />
}

export function FieldGroup({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
    return <div data-slot="control" {...props} className={classNames(className, 'space-y-6')} />
}

export function Field({ className, ...props }: { className?: string } & Omit<Headless.FieldProps, 'className'>) {
    return (
        <Headless.Field
            {...props}
            className={classNames(
                className,
                '[&>[data-slot=label]+[data-slot=control]]:mt-1',
                '[&>[data-slot=label]+[data-slot=description]]:mt-1',
                '[&>[data-slot=description]+[data-slot=control]]:mt-3',
                '[&>[data-slot=control]+[data-slot=description]]:mt-3',
                '[&>[data-slot=control]+[data-slot=error]]:mt-3'
            )}
        />
    )
}

/**
 *
 * @param headlessUi If false, a regular <label> element is rendered. If true, a Headless UI <Label> component is rendered. This is useful when you don't want to nest your label in a <Field> component, but still want to use the label styles.
 * @param className
 * @param value
 * @param children
 * @param htmlFor
 * @param props
 */
export function Label({
    headlessUi = false,
    className,
    value,
    children,
    htmlFor,
    ...props
}: {
    headlessUi?: boolean
    className?: string
    value?: string
    htmlFor?: string
    children?: React.ReactNode
}) {
    const Component = headlessUi ? Headless.Label : 'label'

    return (
        <Component
            data-slot="label"
            {...(htmlFor ? { htmlFor } : {})}
            {...props}
            className={classNames(className, 'text-sm/5 font-semibold tracking-wide text-stone-500 select-none data-disabled:opacity-50')}
        >
            {children || value}
        </Component>
    )
}

export function SectionHeading({ className, children, size = 'md', ...props }: { className?: string; size?: string; children: React.ReactNode }) {
    let sizeClass = ''
    if (size === 'sm') {
        sizeClass = 'text-sm/6 text-base/6'
    } else if (size === 'md') {
        sizeClass = 'text-lg/5 sm:text-base/6'
    } else if (size === 'lg') {
        sizeClass = 'text-xl/6 sm:text-lg/5'
    }
    return (
        <div
            data-slot="label"
            {...props}
            className={classNames(className, sizeClass, 'mb-5 font-bold text-stone-800 select-none data-disabled:opacity-50')}
        >
            {children}
        </div>
    )
}

export function ErrorMessage({ className, message, ...props }: { className?: string; message?: unknown } & React.ComponentPropsWithoutRef<'div'>) {
    const normalizedMessages = normalizeErrorMessages(message)

    if (normalizedMessages.length === 0) {
        return null
    }

    return (
        <div data-slot="error" {...props} className={classNames(className, 'text-base/6 text-red-600 data-disabled:opacity-50 sm:text-sm/4')}>
            {normalizedMessages.length === 1 ? (
                normalizedMessages[0]
            ) : (
                <ul className="list-disc space-y-0.5 pl-5">
                    {normalizedMessages.map((normalizedMessage, index) => (
                        <li key={`${normalizedMessage}-${index}`}>{normalizedMessage}</li>
                    ))}
                </ul>
            )}
        </div>
    )
}

export function normalizeErrorMessages(message: unknown): string[] {
    if (message === null || message === undefined || message === '') {
        return []
    }

    const messagesArray = Array.isArray(message) ? message : [message]

    return messagesArray.flatMap((messageEntry) => {
        if (typeof messageEntry === 'string') {
            return messageEntry
                .split(/\r?\n/)
                .map((line) => line.trim())
                .filter((line) => line !== '')
        }

        if (messageEntry === null || messageEntry === undefined) {
            return []
        }

        return [String(messageEntry)]
    })
}
